/**
 * Krépite search core - one normalization contract for every active search entry point.
 * OEM identity mirrors scripts/catalog_search_quality.py::normalize_oem_reference.
 * Dimension identity is metric and deterministic: mm internally, cm converted to mm.
 */
(function (global) {
  'use strict';

  if (global.KrepiteSearch && global.KrepiteSearch.version === '2026-10-14-v5') return;

  var script = document.currentScript;
  var finderDataUrl = script && script.dataset ? script.dataset.finderUrl || '' : '';
  // Index des fiches publiees hors donnee Finder (Dixneuf, Ten, futurs imports),
  // genere par scripts/build_catalog_search_index.py. Charge a la PREMIERE
  // recherche seulement, jamais au chargement de la page.
  var catalogIndexUrl = script && script.dataset ? script.dataset.catalogUrl || '' : '';
  var finderDataPromise = null;
  var catalogIndexPromise = null;
  var catalogBrandLabels = {};
  var indexPromise = null;
  var inputTimers = new WeakMap();

  /* 02/11 (lot 4, perf page recherche) : le chargement renormalisait les memes titres des
     102 modeles a chaque appel (profil CPU x4 : normalizeText 279 ms, deaccent 185 ms).
     Fonctions pures : un cache borne rend le meme resultat, calcule une fois. */
  var CACHE_MAX = 50000;
  function memoiser(fn) {
    var cache = new Map();
    return function (value) {
      var cle = value == null ? '' : String(value);
      var connu = cache.get(cle);
      if (connu !== undefined) return connu;
      var resultat = fn(cle);
      if (cache.size >= CACHE_MAX) cache.clear();
      cache.set(cle, resultat);
      return resultat;
    };
  }

  var deaccent = memoiser(function (value) {
    return value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '');
  });

  // 30/10 : noms de modeles ecrits en lettres (Charnwood C-Five) et cherches en chiffres
  // (« c5 », « c 5 ») : meme forme des deux cotes. « ten » reste un mot (fournisseur Ten).
  var NOMBRES_EN_LETTRES = { one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9' };
  // 04/11 (Florian : « island III » et « ISLAND 3 » doivent trouver la meme chose) : chiffres romains de 2 a 9
  // ecrits seuls. I, V et X restent des lettres (« 230 V », « Island I AP » s'ecrivent aussi ainsi).
  var ROMAINS = { ii: '2', iii: '3', iv: '4', vi: '6', vii: '7', viii: '8', ix: '9' };

  var normalizeText = memoiser(function (value) {
    return deaccent(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\b(one|two|three|four|five|six|seven|eight|nine)\b/g, function (mot) { return NOMBRES_EN_LETTRES[mot]; })
      .replace(/\b(ii|iii|iv|vi|vii|viii|ix)\b/g, function (mot) { return ROMAINS[mot]; })
      .replace(/\b([a-z])(\d+)\b/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
  });

  function normalizeLoose(value) {
    return normalizeText(value).replace(/\s+/g, '');
  }

  function normalizeOem(value) {
    return deaccent(value)
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '');
  }

  function stripReferencePrefix(value) {
    return deaccent(value)
      .replace(/^\s*(?:ref(?:erence)?|oem|sku)\s*[:#=\-]?\s*/i, '')
      .trim();
  }

  function normalizeOemQuery(value) {
    return normalizeOem(stripReferencePrefix(value));
  }

  function isAdjacentTranspose(a, b) {
    if (a.length !== b.length) return false;
    var diffs = [];
    for (var i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) diffs.push(i);
      if (diffs.length > 2) return false;
    }
    return diffs.length === 2 &&
      diffs[1] === diffs[0] + 1 &&
      a[diffs[0]] === b[diffs[1]] &&
      a[diffs[1]] === b[diffs[0]];
  }

  function editDistanceWithin(a, b, maxDistance) {
    a = String(a || '');
    b = String(b || '');
    var max = Math.max(0, Number(maxDistance) || 0);
    if (Math.abs(a.length - b.length) > max) return max + 1;
    if (a === b) return 0;
    if (isAdjacentTranspose(a, b)) return 1;

    var previous = [];
    for (var j = 0; j <= b.length; j += 1) previous[j] = j;

    for (var i = 1; i <= a.length; i += 1) {
      var current = [i];
      var rowMin = current[0];
      for (var k = 1; k <= b.length; k += 1) {
        var cost = a[i - 1] === b[k - 1] ? 0 : 1;
        current[k] = Math.min(
          current[k - 1] + 1,
          previous[k] + 1,
          previous[k - 1] + cost
        );
        if (current[k] < rowMin) rowMin = current[k];
      }
      if (rowMin > max) return max + 1;
      previous = current;
    }
    return previous[b.length];
  }

  /* Meme regle que fuzzyTokenMatch (hors sous-chaine, testee a part) : les mots
     du vocabulaire qu'un jeton valide, par prefixe ou distance d'edition. */
  function motsQuiValident(token, vocabulaire) {
    var needle = normalizeText(token);
    var valides = new Set();
    if (!needle || needle.length < 4) return valides;
    var maxDistance = needle.length >= 7 ? 2 : 1;
    vocabulaire.forEach(function (word) {
      var ok = (word.indexOf(needle) === 0 || needle.indexOf(word) === 0)
        ? Math.min(word.length, needle.length) >= 4
        : editDistanceWithin(needle, word, maxDistance) <= maxDistance;
      if (ok) valides.add(word);
    });
    return valides;
  }

  var textesSource = null;
  var textesCache = null;
  function textesDesPieces(parts) {
    if (textesSource === parts && textesCache) return textesCache;
    textesSource = parts;
    textesCache = parts.map(function (part) {
      var strong = normalizeText([part.title, part.category, part.category_label, part.reference_oem].join(' '));
      var context = normalizeText([part.model_label, part.brand_handle].join(' '));
      var full = normalizeText(strong + ' ' + context);
      return { strong: strong, context: context, full: full, mots: full.split(' ').filter(Boolean), category: normalizeText(part.category) };
    });
    var vocabulaire = new Set();
    textesCache.forEach(function (texte) { texte.mots.forEach(function (mot) { vocabulaire.add(mot); }); });
    textesCache.vocabulaire = Array.from(vocabulaire);
    return textesCache;
  }

  /* Familles (handles) que la requete nomme, dans l'ordre des mots : un jeton de 4
     lettres ou plus qui commence un mot du libelle de la famille ou d'une de ses
     sous-familles (« joint » nomme « Joints »). Le premier nom est la tete de la
     requete en francais (« joint de porte »). Donnee : index `familles` du pipeline. */
  function famillesCitees(tokens, familles) {
    var nommees = [];
    if (!familles || typeof familles !== 'object') return nommees;
    var libelles = Object.keys(familles).map(function (handle) {
      var famille = familles[handle] || {};
      var sous = famille.sous_familles && typeof famille.sous_familles === 'object' ? Object.keys(famille.sous_familles).map(function (k) { return famille.sous_familles[k]; }) : [];
      var mots = normalizeText([famille.label || handle].concat(sous).join(' ')).split(' ').filter(function (mot) { return mot.length >= 4; });
      return { handle: handle, tete: normalizeText(famille.label || handle).split(' ')[0] || '', mots: mots };
    });
    tokens.forEach(function (token) {
      var needle = normalizeText(token);
      if (needle.length < 4) return;
      // D'abord la famille dont c'est le nom propre (« Joints »), puis celles qui le citent.
      var directes = libelles.filter(function (l) { return l.tete.indexOf(needle) === 0 || needle.indexOf(l.tete) === 0 && l.tete.length >= 4; });
      var citees = directes.length ? directes : libelles.filter(function (l) { return l.mots.some(function (mot) { return mot.indexOf(needle) === 0; }); });
      citees.forEach(function (l) { if (nommees.indexOf(l.handle) === -1) nommees.push(l.handle); });
    });
    return nommees;
  }

  /* 04/11 (P1 13) : famille nommee DIRECTEMENT par le premier mot qui en nomme une (« joint » pour Joints), jamais par
     une sous-famille qui la cite (« vitre » n'est pas Joints parce que « Joint de vitre » existe). Sert au filtre strict. */
  function familleNommeeEnTete(tokens, familles) {
    if (!familles || typeof familles !== 'object') return '';
    var tetes = Object.keys(familles).map(function (handle) {
      return { handle: handle, tete: normalizeText((familles[handle] || {}).label || handle).split(' ')[0] || '' };
    });
    for (var i = 0; i < tokens.length; i += 1) {
      var needle = normalizeText(tokens[i]);
      if (needle.length < 4) continue;
      var directe = tetes.filter(function (l) { return l.tete.length >= 4 && (l.tete.indexOf(needle) === 0 || needle.indexOf(l.tete) === 0); })[0];
      if (directe) return directe.handle;
    }
    return '';
  }

  function fuzzyTokenMatch(token, haystack) {
    var needle = normalizeText(token);
    var full = normalizeText(haystack);
    if (!needle) return true;
    if (full.indexOf(needle) !== -1) return true;
    if (needle.length < 4) return false;

    var words = full.split(' ').filter(Boolean);
    var maxDistance = needle.length >= 7 ? 2 : 1;
    return words.some(function (word) {
      if (word.indexOf(needle) === 0 || needle.indexOf(word) === 0) {
        return Math.min(word.length, needle.length) >= 4;
      }
      return editDistanceWithin(needle, word, maxDistance) <= maxDistance;
    });
  }

  function numeric(raw) {
    var value = Number(String(raw == null ? '' : raw).replace(',', '.'));
    return Number.isFinite(value) ? value : null;
  }

  function toMm(raw, unit) {
    var value = numeric(raw);
    if (value == null) return null;
    return String(unit || 'mm').toLowerCase() === 'cm' ? value * 10 : value;
  }

  function dimensionResidue(raw, matched) {
    var rest = String(raw || '');
    if (matched) rest = rest.replace(matched, ' ');
    return normalizeText(rest)
      .split(' ')
      .filter(function (term) {
        return term && [
          'taille','dimension','dimensions','mesure','mesures',
          'diametre','diameter','dia','longueur','largeur','hauteur','epaisseur',
          'length','width','height','thickness','mm','cm'
        ].indexOf(term) === -1;
      })
      .join(' ');
  }

  function parseDimensionQuery(value) {
    var raw = deaccent(value).replace(/×/g, 'x').replace(/,/g, '.').trim();
    if (!raw) return null;
    var match;

    match = raw.match(/\bM\s*(\d{1,2}(?:\.\d+)?)\s*x\s*(\d{1,4}(?:\.\d+)?)\s*(mm|cm)?\b/i);
    if (!match) {
      match = raw.match(/\bM\s*(\d{1,2}(?:\.\d+)?)\s+(\d{1,4}(?:\.\d+)?)\s*(mm|cm)\b/i);
    }
    if (match) {
      return {
        kind: 'thread',
        thread_mm: numeric(match[1]),
        length_mm: toMm(match[2], match[3] || 'mm'),
        terms: dimensionResidue(raw, match[0]),
        raw: String(value || '').trim()
      };
    }

    match = raw.match(/(?:Ø|ø|\bdiam(?:eter|etre)?\.?|\bdia\.?)\s*[:=]?\s*(\d{1,4}(?:\.\d+)?)\s*(mm|cm)?\b/i);
    if (!match) {
      match = raw.match(/\b(\d{1,4}(?:\.\d+)?)\s*(mm|cm)\s*(?:dia(?:meter|metre)?\.?)\b/i);
    }
    if (match) {
      return {
        kind: 'diameter',
        diameter_mm: toMm(match[1], match[2] || 'mm'),
        terms: dimensionResidue(raw, match[0]),
        raw: String(value || '').trim()
      };
    }

    match = raw.match(/\b(longueur|largeur|hauteur|epaisseur|length|width|height|thickness)\s*[:=]?\s*(\d{1,4}(?:\.\d+)?)\s*(mm|cm)?\b/i);
    if (match) {
      var fields = {
        longueur: 'length_mm', length: 'length_mm',
        largeur: 'width_mm', width: 'width_mm',
        hauteur: 'height_mm', height: 'height_mm',
        epaisseur: 'thickness_mm', thickness: 'thickness_mm'
      };
      return {
        kind: 'labeled',
        field: fields[String(match[1]).toLowerCase()],
        value_mm: toMm(match[2], match[3] || 'mm'),
        terms: dimensionResidue(raw, match[0]),
        raw: String(value || '').trim()
      };
    }

    match = raw.match(/\b(\d{1,4}(?:\.\d+)?)\s*(mm|cm)?\s*x\s*(\d{1,4}(?:\.\d+)?)\s*(mm|cm)?\s*x\s*(\d{1,4}(?:\.\d+)?)\s*(mm|cm)?\b/i);
    if (match) {
      var common3 = match[6] || match[4] || match[2] || 'mm';
      return {
        kind: 'vector3',
        values_mm: [
          toMm(match[1], match[2] || common3),
          toMm(match[3], match[4] || common3),
          toMm(match[5], match[6] || common3)
        ],
        terms: dimensionResidue(raw, match[0]),
        raw: String(value || '').trim()
      };
    }

    match = raw.match(/\b(\d{1,4}(?:\.\d+)?)\s*(mm|cm)?\s*x\s*(\d{1,4}(?:\.\d+)?)\s*(mm|cm)?\b/i);
    if (match) {
      var common2 = match[4] || match[2] || 'mm';
      return {
        kind: 'vector2',
        values_mm: [
          toMm(match[1], match[2] || common2),
          toMm(match[3], match[4] || common2)
        ],
        terms: dimensionResidue(raw, match[0]),
        raw: String(value || '').trim()
      };
    }

    match = raw.match(/\b(\d{1,4}(?:\.\d+)?)\s*(mm|cm)\b/i);
    if (match) {
      return {
        kind: 'scalar',
        value_mm: toMm(match[1], match[2]),
        terms: dimensionResidue(raw, match[0]),
        raw: String(value || '').trim()
      };
    }

    return null;
  }

  function looksDimension(value) {
    return Boolean(parseDimensionQuery(value));
  }

  function looksTechnical(value) {
    if (looksDimension(value)) return false;
    var raw = stripReferencePrefix(value);
    if (!raw) return false;
    var compact = normalizeOem(raw);
    if (compact.length < 4 || compact.length > 32 || !/[0-9]/.test(compact)) return false;

    var words = normalizeText(raw).split(' ').filter(Boolean);
    if (words.length === 1) return true;
    if (words.length > 2) return false;
    if (/[\/._-]/.test(raw)) return true;

    var first = words[0] || '';
    var second = words[1] || '';
    return first.length <= 3 || /^\d+$/.test(first) || (/\d/.test(first) && /\d/.test(second));
  }

  function approxEqual(a, b) {
    return Number.isFinite(Number(a)) && Number.isFinite(Number(b)) && Math.abs(Number(a) - Number(b)) <= 0.05;
  }

  function mergeDimensions(base, extra) {
    var out = {};
    Object.keys(base || {}).forEach(function (key) {
      var value = numeric(base[key]);
      if (value != null && value > 0) out[key] = value;
    });
    Object.keys(extra || {}).forEach(function (key) {
      var value = numeric(extra[key]);
      if (value != null && value > 0 && out[key] == null) out[key] = value;
    });
    return out;
  }

  function extractDimensionsFromText(value) {
    var text = deaccent(value).replace(/×/g, 'x').replace(/,/g, '.');
    var dims = {};
    var parsed = parseDimensionQuery(text);
    if (parsed) {
      if (parsed.kind === 'thread') {
        dims.thread_mm = parsed.thread_mm;
        dims.length_mm = parsed.length_mm;
      } else if (parsed.kind === 'diameter') {
        dims.diameter_mm = parsed.diameter_mm;
      } else if (parsed.kind === 'labeled') {
        dims[parsed.field] = parsed.value_mm;
      } else if (parsed.kind === 'vector2') {
        dims.length_mm = parsed.values_mm[0];
        dims.width_mm = parsed.values_mm[1];
      } else if (parsed.kind === 'vector3') {
        dims.length_mm = parsed.values_mm[0];
        dims.width_mm = parsed.values_mm[1];
        dims.thickness_mm = parsed.values_mm[2];
      } else if (parsed.kind === 'scalar') {
        dims.scalar_mm = parsed.value_mm;
      }
    }

    var diameter = text.match(/(?:Ø|ø|diam(?:eter|etre)?\.?\s*)\s*(\d{1,4}(?:\.\d+)?)\s*(?:mm)?(?:\s*x\s*(\d{1,4}(?:\.\d+)?)\s*(?:mm)?)?/i);
    if (!diameter) diameter = text.match(/(\d{1,4}(?:\.\d+)?)\s*mm\s*(?:dia(?:meter|metre)?\.?)\b/i);
    if (diameter) {
      dims.diameter_mm = numeric(diameter[1]);
      if (diameter[2]) dims.thickness_mm = numeric(diameter[2]);
    }

    var scalars = [];
    var scalarRe = /(\d{1,4}(?:\.\d+)?)\s*mm\b/ig;
    var scalarMatch;
    while ((scalarMatch = scalarRe.exec(text)) !== null) {
      var scalar = numeric(scalarMatch[1]);
      if (scalar != null && !scalars.some(function (value) { return approxEqual(value, scalar); })) scalars.push(scalar);
    }
    dims._scalars = scalars;
    return dims;
  }

  var dimensionAliases = [
    ['joint','joints','seal','seals','gasket','gaskets','rope'],
    ['vitre','vitres','vitrage','glass'],
    ['vis','visserie','screw','screws','bolt','bolts'],
    ['rondelle','rondelles','washer','washers'],
    ['ecrou','ecrous','nut','nuts'],
    ['goupille','goupilles','pin','pins'],
    ['ressort','ressorts','spring','springs'],
    ['aimant','magnet'],
    ['plaque','plaques','plate','plates'],
    ['vermiculite','isolation','insulation'],
    ['deflecteur','deflecteurs','baffle'],
    ['grille','grilles','grate']
  ];

  function termMatchesPart(term, haystack) {
    if (haystack.indexOf(term) !== -1) return true;
    for (var i = 0; i < dimensionAliases.length; i += 1) {
      var group = dimensionAliases[i];
      if (group.indexOf(term) !== -1 && group.some(function (alias) { return haystack.indexOf(alias) !== -1; })) return true;
    }
    return false;
  }

  function dimensionTermsMatch(part, query) {
    var terms = normalizeText(query.terms || '').split(' ').filter(Boolean);
    if (!terms.length) return true;
    var haystack = normalizeText([
      part.title,
      part.category,
      part.model_label,
      part.brand_handle
    ].filter(Boolean).join(' '));
    return terms.every(function (term) { return termMatchesPart(term, haystack); });
  }

  function partMatchesDimension(part, query) {
    if (!query || !dimensionTermsMatch(part, query)) return false;
    var dims = mergeDimensions(part.dimensions || {}, extractDimensionsFromText(part.title || ''));
    var scalars = Array.isArray(dims._scalars) ? dims._scalars.slice() : [];
    Object.keys(dims).forEach(function (key) {
      if (key === '_scalars' || key === 'thread_mm') return;
      var value = numeric(dims[key]);
      if (value != null && !scalars.some(function (item) { return approxEqual(item, value); })) scalars.push(value);
    });

    if (query.kind === 'thread') {
      return approxEqual(dims.thread_mm, query.thread_mm) && approxEqual(dims.length_mm, query.length_mm);
    }
    if (query.kind === 'diameter') {
      return approxEqual(dims.diameter_mm, query.diameter_mm);
    }
    if (query.kind === 'labeled') {
      return approxEqual(dims[query.field], query.value_mm);
    }
    if (query.kind === 'scalar') {
      return scalars.some(function (value) { return approxEqual(value, query.value_mm); });
    }
    if (query.kind === 'vector2') {
      var a = numeric(dims.length_mm);
      var b = numeric(dims.width_mm);
      if (a == null || b == null) return false;
      return (
        (approxEqual(a, query.values_mm[0]) && approxEqual(b, query.values_mm[1])) ||
        (approxEqual(a, query.values_mm[1]) && approxEqual(b, query.values_mm[0]))
      );
    }
    if (query.kind === 'vector3') {
      var l = numeric(dims.length_mm);
      var w = numeric(dims.width_mm);
      var t = numeric(dims.thickness_mm);
      if (l == null || w == null || t == null) return false;
      return (
        ((approxEqual(l, query.values_mm[0]) && approxEqual(w, query.values_mm[1])) ||
         (approxEqual(l, query.values_mm[1]) && approxEqual(w, query.values_mm[0]))) &&
        approxEqual(t, query.values_mm[2])
      );
    }
    return false;
  }

  // 02/11 : relu une fois par adresse (147 ms au profil CPU x4 : un URLSearchParams par piece).
  var apercuLu = { recherche: null, id: '' };
  function currentPreviewThemeId() {
    var recherche = '';
    try { recherche = global.location.search; } catch (_e) { return ''; }
    if (recherche === apercuLu.recherche) return apercuLu.id;
    var id = '';
    try { id = new URLSearchParams(recherche).get('preview_theme_id') || ''; } catch (_e) { id = ''; }
    apercuLu = { recherche: recherche, id: id };
    return id;
  }

  function withPreview(url) {
    var preview = currentPreviewThemeId();
    if (!preview) return url;
    var parsed = new URL(url, global.location.origin);
    parsed.searchParams.set('preview_theme_id', preview);
    return parsed.pathname + parsed.search + parsed.hash;
  }

  // Affichage commerce d'une piece, lu UNIQUEMENT dans la projection canonique
  // (finder-data, produite par le pipeline). Un montant n'est affiche que s'il
  // est valide Shopify et > 0 (meme regle que la confirmation du Finder guide).
  // Une piece canonique sans prix valide suit le repli de la PDP : sur devis.
  // Hors projection, aucun statut commerce n'est affirme. Jamais le prix brut
  // d'un endpoint Shopify : les pieces sur devis y portent un placeholder 0.00.
  function commerceDisplay(part) {
    var preview = part && part.product_preview;
    var price = preview && preview.price;
    if (price && price.source === 'shopify_validated' && typeof price.amount === 'number'
        && price.amount > 0 && price.formatted) {
      return { kind: 'price', label: String(price.formatted) };
    }
    if (part && part.canonical_key) return { kind: 'quote', label: 'Sur devis' };
    return { kind: 'unknown', label: '' };
  }

  function resolvePartUrl(part) {
    var url = '';
    if (part && part.canonical_target_published && part.canonical_url) {
      url = part.canonical_url;
    } else if (part && part.product_preview && part.product_preview.url) {
      url = part.product_preview.url;
    } else if (part && part.product_handle) {
      url = '/products/' + part.product_handle;
    }
    return url ? withPreview(url) : '';
  }

  // Contrat du pipeline (31/10) : `famille` et `sous_famille` (handles) sur chaque piece.
  // Copies seulement s'ils existent : absents, la recherche regroupe par repli ; une
  // sous_famille null (« Autres ») reste null.
  function copierFamille(source, cible) {
    if (!source || !cible) return cible;
    if (source.famille !== undefined) cible.famille = source.famille == null ? '' : String(source.famille);
    if (source.sous_famille !== undefined) cible.sous_famille = source.sous_famille == null ? null : String(source.sous_famille);
    return cible;
  }

  function flattenFinderData(data) {
    var models = data && data.models && typeof data.models === 'object' ? data.models : {};
    var parts = [];
    Object.keys(models).sort().forEach(function (modelHandle) {
      var model = models[modelHandle] || {};
      var candidates = Array.isArray(model.candidate_parts) ? model.candidate_parts : [];
      candidates.forEach(function (part) {
        var reference = String(part.reference_oem || '').trim();
        var key = normalizeOem(reference);
        if (!key) return;
        parts.push({
          key: key,
          reference_oem: reference,
          title: String(part.title || part.selection_label || reference),
          category: String(part.category || ''),
          dimensions: mergeDimensions(part.dimensions || {}, extractDimensionsFromText(part.title || part.selection_label || '')),
          product_handle: String(part.product_handle || ''),
          product_preview: part.product_preview || null,
          schema_image_filename: String(part.schema_image_filename || ''),
          schema_image_width_px: Number(part.schema_image_width_px) || 0,
          schema_image_height_px: Number(part.schema_image_height_px) || 0,
          highlight: part.highlight || null,
          url: resolvePartUrl(part),
          canonical_key: String(part.canonical_key || ''),
          model_handle: modelHandle,
          model_label: String(model.model_label || modelHandle),
          brand_handle: String(model.brand || ''),
          schema_handle: String(part.schema_handle || ''),
          schema_number: part.schema_number == null ? '' : String(part.schema_number),
          repere: part.repere == null ? '' : String(part.repere)
        });
        copierFamille(part, parts[parts.length - 1]);
      });
    });
    return parts;
  }

  function dedupeParts(parts) {
    var seen = new Set();
    return parts.filter(function (part) {
      var id = part.canonical_key || ((part.brand_handle || '') + ':' + part.key);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  /* PROJECTION COMPACTE (krepite.finder-asset-compact/1, P0 Finder 2026-10-13).
     Une piece par ligne, une table par schema, champs deductibles omis.
     Miroir EXACT de scripts/finder_asset_compact.py `rehydrater` : un test Node
     verifie que le resultat egale data/identification/finder-data.v1.json. */
  var LEGACY = 1, CIBLE_PUBLIEE = 2, SURLIGNAGE_ACTIF = 4;
  // 02/11 : largeur et hauteur de la planche (pipeline #1596), pour le recadrage par le CDN.
  // 03/11 : zone de dessin de la planche (pipeline #1643), oubliee ici : le test Node de la
  // rehydratation echouait et son diff geant saturait la memoire (13,7 Go).
  var SCHEMA_CHAMPS = ['schema_number', 'schema_image_filename', 'schema_zone_label', 'schema_image_width_px', 'schema_image_height_px', 'schema_zone_dessin'];

  function pieceDerivee(l, schemas, taxonomies) {
    var repere = l[6], apercu = l[9], titre = l[1], handle = l[3], drapeaux = l[7] || 0;
    var piece = {
      selection_label: (repere !== null && repere !== undefined && repere !== '' && apercu !== null && apercu !== undefined)
        ? 'Repère ' + repere + ' - ' + apercu : titre,
      category: l[2],
      title: titre,
      product_handle: handle,
      highlight: { enabled: Boolean(drapeaux & SURLIGNAGE_ACTIF) },
      reference_oem: l[0],
      canonical_key: l[4],
      canonical_handle: handle,
      canonical_url: '/products/' + handle,
      handle_is_legacy: Boolean(drapeaux & LEGACY),
      canonical_target_published: Boolean(drapeaux & CIBLE_PUBLIEE)
    };
    if (l[8] !== null && l[8] !== undefined) { piece.highlight.x_pct = l[8][0]; piece.highlight.y_pct = l[8][1]; }
    if (apercu !== null && apercu !== undefined) piece.product_preview = { url: '/products/' + handle, title: apercu };
    if (repere !== null && repere !== undefined) piece.repere = repere;
    if (l[5] !== null && l[5] !== undefined) {
      piece.schema_handle = l[5];
      var s = schemas[l[5]] || [];
      SCHEMA_CHAMPS.forEach(function (champ, i) { piece[champ] = s[i]; });
    }
    // Colonne 11 (31/10) : indice dans la table `taxonomies` ([famille, sous_famille] en handles).
    if (l[11] !== null && l[11] !== undefined && taxonomies[l[11]]) {
      piece.famille = taxonomies[l[11]][0];
      piece.sous_famille = taxonomies[l[11]][1];
    }
    var ecarts = l[10];
    if (ecarts) {
      Object.keys(ecarts).forEach(function (cle) {
        if (cle === '-') ecarts[cle].forEach(function (absente) { delete piece[absente]; });
        else if (cle === 'highlight+') Object.assign(piece.highlight, ecarts[cle]);
        else piece[cle] = ecarts[cle];
      });
    }
    return piece;
  }

  function rehydrateFinderData(data) {
    if (!data || data.format !== 'krepite.finder-asset-compact/1') return data;
    var schemas = data.schemas || {};
    var taxonomies = data.taxonomies || [];
    var sortie = {};
    Object.keys(data).forEach(function (cle) {
      if (['format', 'columns', 'schemas', 'taxonomies', 'models'].indexOf(cle) === -1) sortie[cle] = data[cle];
    });
    sortie.models = {};
    Object.keys(data.models || {}).forEach(function (handle) {
      var meta = data.models[handle] || {};
      var modele = {};
      Object.keys(meta).forEach(function (cle) { if (cle !== 'p') modele[cle] = meta[cle]; });
      modele.candidate_parts = (meta.p || []).map(function (l) { return pieceDerivee(l, schemas, taxonomies); });
      sortie.models[handle] = modele;
    });
    return sortie;
  }

  // UN SEUL chargement par page : tous les consommateurs (recherche, tunnel,
  // Finder guide, page schema, visuel de repli) passent par cette promesse.
  function loadFinderData() {
    if (finderDataPromise) return finderDataPromise;
    if (!finderDataUrl) return Promise.resolve({ brands: [], models: {} });
    finderDataPromise = fetch(finderDataUrl, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('finder-data HTTP ' + response.status);
        return response.json();
      })
      .then(rehydrateFinderData)
      .catch(function () { return { brands: [], models: {} }; });
    return finderDataPromise;
  }

  var EMPTY_CATALOG = { brands: {}, sectors: {}, categories: {}, familles: {}, parts: [] };

  function flattenCatalogIndex(data) {
    if (!data || data.format !== 'krepite.catalog-search-index/1') return EMPTY_CATALOG;
    var columns = Array.isArray(data.columns) ? data.columns : [];
    var col = {};
    columns.forEach(function (name, i) { col[name] = i; });
    var brands = data.brands || {};
    var categories = data.categories || {};
    var parts = (Array.isArray(data.rows) ? data.rows : []).map(function (row) {
      var handle = String(row[col.handle] || '');
      var reference = String(row[col.reference] || '').trim();
      var title = String(row[col.title] || reference || handle);
      var brand = String(row[col.brand] || '');
      var category = String(row[col.category] || '');
      var brandLabel = String(brands[brand] || brand);
      var categoryLabel = String((categories[category] && categories[category].label) || '');
      var part = {
        key: normalizeOem(reference),
        reference_oem: reference,
        title: title,
        category: category,
        category_label: categoryLabel,
        dimensions: extractDimensionsFromText(title),
        product_handle: handle,
        product_preview: null,
        url: handle ? withPreview('/products/' + handle) : '',
        canonical_key: String(row[col.canonical_key] || ''),
        model_handle: '',
        model_label: [brandLabel, categoryLabel].filter(Boolean).join(' · '),
        brand_handle: brand,
        sector_handle: String(row[col.sector] || ''),
        schema_handle: '',
        schema_number: '',
        repere: ''
      };
      if (col.famille !== undefined) part.famille = String(row[col.famille] || '');
      if (col.sous_famille !== undefined) part.sous_famille = row[col.sous_famille] == null || row[col.sous_famille] === '' ? null : String(row[col.sous_famille]);
      return part;
    });
    return { brands: brands, sectors: data.sectors || {}, categories: categories, familles: data.familles || {}, parts: parts };
  }

  // Meme regle qu'au Finder : un echec reseau rend un index vide, jamais une erreur.
  function loadCatalogIndex() {
    if (catalogIndexPromise) return catalogIndexPromise;
    if (!catalogIndexUrl) return Promise.resolve(EMPTY_CATALOG);
    catalogIndexPromise = fetch(catalogIndexUrl, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('catalog-search-index HTTP ' + response.status);
        return response.json();
      })
      .then(flattenCatalogIndex)
      .catch(function () { return EMPTY_CATALOG; })
      .then(function (catalog) {
        Object.keys(catalog.brands || {}).forEach(function (handle) {
          catalogBrandLabels[handle] = String(catalog.brands[handle] || handle);
        });
        return catalog;
      });
    return catalogIndexPromise;
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;
    indexPromise = Promise.all([loadFinderData().then(flattenFinderData), loadCatalogIndex()])
      .then(function (loaded) { return loaded[0].concat(loaded[1].parts); });
    return indexPromise;
  }

  function lookupExactOem(query) {
    var key = normalizeOemQuery(query);
    if (!key) return Promise.resolve([]);
    return loadIndex().then(function (parts) {
      return dedupeParts(parts.filter(function (part) { return part.key === key; }));
    });
  }

  function lookupOemPrefix(query, limit) {
    var key = normalizeOemQuery(query);
    var max = Math.max(1, Number(limit) || 8);
    if (key.length < 2) return Promise.resolve([]);
    return loadIndex().then(function (parts) {
      var exact = [];
      var prefix = [];
      parts.forEach(function (part) {
        if (part.key === key) exact.push(part);
        else if (part.key.indexOf(key) === 0) prefix.push(part);
      });
      return dedupeParts(exact.concat(prefix)).slice(0, max);
    });
  }

  function lookupOemSmart(query, limit) {
    var key = normalizeOemQuery(query);
    var max = Math.max(1, Number(limit) || 8);
    if (key.length < 3) return Promise.resolve([]);
    return loadIndex().then(function (parts) {
      var ranked = [];
      parts.forEach(function (part, index) {
        var score = 0;
        if (part.key === key) score = 240;
        else if (part.key.indexOf(key) === 0) score = 210;
        else if (key.length >= 5 && part.key.indexOf(key) !== -1) score = 180;
        // 01/11 (audit 30 profils) : fragment de 4 signes lettres + chiffres (« PV19 » dans
        // « 006/PV19 ») : assez distinctif pour chercher au milieu de la reference.
        else if (key.length === 4 && /[a-z]/i.test(key) && /[0-9]/.test(key) && part.key.indexOf(key) !== -1) score = 175;
        else if (key.length >= 5) {
          var maxDistance = key.length >= 9 ? 2 : 1;
          var distance = editDistanceWithin(key, part.key, maxDistance);
          if (distance <= maxDistance) score = 155 - (distance * 10);
        }
        if (!score) return;
        ranked.push(Object.assign({}, part, {
          kind: 'part',
          score: score - index / 100000,
          meta: 'Réf. ' + part.reference_oem + (part.model_label ? ' · ' + part.model_label : ''),
          match_type: part.key === key ? 'exact-oem' : 'oem-suggestion'
        }));
      });
      return dedupeParts(ranked
        .sort(function (a, b) { return b.score - a.score; }))
        .slice(0, max);
    });
  }

  function lookupDimensions(query, limit) {
    var parsed = typeof query === 'string' ? parseDimensionQuery(query) : query;
    var max = Math.max(1, Number(limit) || 50);
    if (!parsed) return Promise.resolve([]);
    return loadIndex().then(function (parts) {
      return dedupeParts(parts.filter(function (part) {
        return partMatchesDimension(part, parsed);
      })).slice(0, max);
    });
  }

  function matchesDimensionText(text, query) {
    var parsed = typeof query === 'string' ? parseDimensionQuery(query) : query;
    if (!parsed) return false;
    return partMatchesDimension({
      title: String(text || ''),
      category: '',
      model_label: '',
      brand_handle: '',
      dimensions: extractDimensionsFromText(text || '')
    }, parsed);
  }

  function brandLabelMap(data) {
    var map = {};
    var brands = Array.isArray(data && data.brands) ? data.brands : [];
    brands.forEach(function (brand) {
      if (!brand || !brand.handle) return;
      map[String(brand.handle)] = String(brand.label || brand.handle);
    });
    return map;
  }

  function modelIdentity(modelHandle, model, brands) {
    var brandHandle = String((model && model.brand) || '');
    var brandLabel = brands[brandHandle] || brandHandle;
    var label = String((model && model.model_label) || modelHandle || '');
    var normalizedLabel = normalizeText(label);
    var normalizedBrand = normalizeText(brandLabel);
    var shortLabel = normalizedLabel;
    if (normalizedBrand && shortLabel.indexOf(normalizedBrand + ' ') === 0) {
      shortLabel = shortLabel.slice(normalizedBrand.length + 1).trim();
    }
    return {
      key: String(modelHandle || ''),
      label: label,
      normalized: normalizedLabel,
      short: shortLabel,
      compact: normalizeLoose(shortLabel),
      brand_handle: brandHandle,
      brand_label: brandLabel
    };
  }

  /* Le modele CITE dans la requete : la mention (nom court ou complet) la plus
     longue, entre mots entiers. `reste` = la requete sans le modele ni la
     marque, pour classer ses pieces (« vitre c four » -> C-Four, « vitre »). */
  function modeleCite(q, models, brands) {
    var meilleur = null;
    Object.keys(models).forEach(function (modelHandle) {
      var identity = modelIdentity(modelHandle, models[modelHandle], brands);
      [identity.normalized, identity.short].forEach(function (phrase) {
        if (!phrase || !phraseInQuery(q, phrase)) return;
        if (!meilleur || phrase.length > meilleur.phrase.length) meilleur = { handle: modelHandle, phrase: phrase, identity: identity };
      });
    });
    if (!meilleur) return null;
    var reste = (' ' + q + ' ').replace(' ' + meilleur.phrase + ' ', ' ');
    var marque = normalizeText(meilleur.identity.brand_label);
    if (marque) reste = reste.replace(' ' + marque + ' ', ' ');
    meilleur.reste = reste.replace(/\s+/g, ' ').trim();
    return meilleur;
  }

  function phraseInQuery(query, phrase) {
    var q = ' ' + normalizeText(query) + ' ';
    var p = normalizeText(phrase);
    return Boolean(p && q.indexOf(' ' + p + ' ') !== -1);
  }

  function resolveCatalogContext(query) {
    var q = normalizeText(query);
    var qLoose = normalizeLoose(query);
    if (!q) return Promise.resolve({});
    return loadFinderData().then(function (data) {
      var brands = brandLabelMap(data);
      var models = data && data.models && typeof data.models === 'object' ? data.models : {};
      var bestModel = null;

      Object.keys(models).forEach(function (modelHandle) {
        var identity = modelIdentity(modelHandle, models[modelHandle], brands);
        if (!identity.short) return;
        var score = 0;
        if (q === identity.short || q === identity.normalized) score = 120;
        else if (qLoose === identity.compact || qLoose === normalizeLoose(identity.normalized)) score = 115;
        else if (phraseInQuery(q, identity.short) || phraseInQuery(q, identity.normalized)) score = 105;
        else if (identity.short.indexOf(q) === 0 && q.length >= 3) score = 80;
        if (!score) return;
        if (!bestModel || score > bestModel.score || (score === bestModel.score && identity.short.length > bestModel.identity.short.length)) {
          bestModel = { score: score, identity: identity };
        }
      });

      if (bestModel && bestModel.score >= 80) {
        var m = bestModel.identity;
        return {
          type: 'model',
          model_key: m.key,
          model_handle: 'model-' + m.key,
          model_label: m.label,
          model_query: m.short,
          brand: m.brand_handle,
          brand_handle: m.brand_handle ? 'brand-' + m.brand_handle : '',
          brand_label: m.brand_label
        };
      }

      var bestBrand = null;
      Object.keys(brands).forEach(function (handle) {
        var label = brands[handle];
        var normalized = normalizeText(label);
        var score = q === normalized || q === normalizeText(handle) ? 100 : phraseInQuery(q, normalized) ? 85 : 0;
        if (score && (!bestBrand || score > bestBrand.score)) {
          bestBrand = { score: score, handle: handle, label: label };
        }
      });
      return bestBrand ? {
        type: 'brand',
        brand: bestBrand.handle,
        brand_handle: 'brand-' + bestBrand.handle,
        brand_label: bestBrand.label
      } : {};
    });
  }

  /* 31/10 : cadrage du contexte de la page (page modele, page marque) ou de « Mon appareil ».
     `cadre` = { model, brand } (handles, avec ou sans prefixe model-/brand-). Un modele ou une
     marque CITES dans la requete l'emportent toujours sur le cadre : « lotus liva 6 » tape sur
     une page Charnwood cherche le Liva 6. Un modele inconnu des donnees retombe sur sa marque.
     Rend { model, brand, famille } effectivement appliques, ou null. */
  function cadreApplicable(q, cadre, models, brands, mention, familles) {
    if (!cadre || mention) return null;
    var marquesCitees = Object.keys(brands).concat(Object.keys(catalogBrandLabels)).some(function (handle) {
      var label = brands[handle] || catalogBrandLabels[handle];
      return phraseInQuery(q, label) || phraseInQuery(q, handle);
    });
    if (marquesCitees) return null;
    var modele = String(cadre.model || '').replace(/^model-/, '');
    if (modele && models[modele]) return { model: modele, brand: String(models[modele].brand || '') };
    var marque = String(cadre.brand || '').replace(/^brand-/, '');
    if (marque && (brands[marque] || catalogBrandLabels[marque])) return { model: '', brand: marque };
    // 31/10 : page famille (taxonomie du pipeline, champ `famille` de chaque piece).
    var famille = String(cadre.famille || '');
    if (famille && familles && familles[famille]) return { model: '', brand: '', famille: famille };
    return null;
  }

  // Le cadre applique voyage avec les resultats (propriete non enumerable : la liste reste
  // identique, element par element, a une recherche sans cadre de meme contenu).
  function avecCadre(liste, applique) {
    Object.defineProperty(liste, 'cadre', { value: applique || null, enumerable: false });
    return liste;
  }

  function lookupText(query, limit, cadre) {
    var q = normalizeText(query);
    var qLoose = normalizeLoose(query);
    var max = Math.max(1, Number(limit) || 8);
    if (q.length < 2) return Promise.resolve([]);

    return Promise.all([loadFinderData(), loadIndex()]).then(function (loaded) {
      var data = loaded[0] || {};
      var parts = loaded[1] || [];
      var brands = brandLabelMap(data);
      var models = data.models && typeof data.models === 'object' ? data.models : {};
      var results = [];
      var bestModel = null;
      var mention = modeleCite(q, models, brands);
      var applique = cadreApplicable(q, cadre, models, brands, mention, data.familles);
      var cadreMarque = applique && !applique.model ? applique.brand : '';
      var cadreFamille = applique && applique.famille ? applique.famille : '';

      Object.keys(models).sort().forEach(function (modelHandle) {
        var model = models[modelHandle] || {};
        var identity = modelIdentity(modelHandle, model, brands);
        if (cadreMarque && identity.brand_handle !== cadreMarque) return;
        var haystack = normalizeText([identity.label, identity.short, modelHandle, identity.brand_label].join(' '));
        var tokens = q.split(' ').filter(Boolean);
        var cite = mention && mention.handle === modelHandle;
        if (!cite && !tokens.every(function (token) { return fuzzyTokenMatch(token, haystack); })) return;

        var score = 75;
        if (q === identity.short || q === identity.normalized) score = 140;
        else if (qLoose === identity.compact || qLoose === normalizeLoose(identity.normalized)) score = 135;
        else if (phraseInQuery(q, identity.short) || phraseInQuery(q, identity.normalized)) score = 125;
        else if (identity.short.indexOf(q) === 0) score = 105;
        // 04/11 : debut du nom complet, marque comprise (« lotus liva » -> Lotus Liva 6), comme « liva » seul.
        else if (identity.normalized.indexOf(q) === 0 && q.indexOf(' ') > 0) score = 105;
        // Modele CITE avec d'autres mots (« vitre c four ») : il cadre la recherche.
        if (cite && score < 125) score = 125;

        var candidates = Array.isArray(model.candidate_parts) ? model.candidate_parts : [];
        var schemaSet = {};
        candidates.forEach(function (part) {
          if (part && part.schema_handle) schemaSet[String(part.schema_handle)] = true;
        });
        results.push({
          kind: 'model',
          score: score,
          title: identity.label,
          // 04/11 (critique Impeccable, P1) : LE nombre de la page modele (nb_references publie avec le modele),
          // sinon les references DISTINCTES ; une piece dessinee sur deux schemas comptait deux fois (113 contre 95).
          meta: (function (n) { return 'Modèle · ' + n + ' pièce' + (n === 1 ? '' : 's') + ' documentée' + (n === 1 ? '' : 's'); })(
            Number(model.nb_references) ||
            Object.keys(candidates.reduce(function (vu, part) { vu[(part && (part.reference_oem || part.canonical_handle || part.product_handle)) || ''] = 1; return vu; }, {})).filter(Boolean).length),
          url: withPreview('/collections/model-' + modelHandle),
          model_handle: modelHandle,
          model_label: identity.label,
          brand_handle: identity.brand_handle,
          // 02/11 : modele nomme dans la requete (« joints liva 6 ») : le panneau le met en tete.
          cite: Boolean(cite)
        });
        if (!bestModel || score > bestModel.score) bestModel = { score: score, handle: modelHandle, identity: identity, model: model };
      });

      // 27/10 : une marque tapee exactement passe devant ses pieces (Ten : 1 751 fiches
      // a ~115 poussaient la marque en 97e position, hors de la liste affichee).
      var BRAND_EXACT_SCORE = 145;
      Object.keys(brands).sort().forEach(function (brandHandle) {
        var label = brands[brandHandle];
        var normalized = normalizeText(label);
        if (q !== normalized && q !== normalizeText(brandHandle)) return;
        results.push({
          kind: 'brand',
          score: BRAND_EXACT_SCORE,
          title: label,
          meta: 'Marque · Voir les modèles documentés',
          url: withPreview('/collections/brand-' + brandHandle),
          brand_handle: brandHandle
        });
      });
      // Marques du catalogue publie sans vue eclatee (Ten, Dixneuf...) : leur
      // page est le Finder filtre sur la marque.
      Object.keys(catalogBrandLabels).sort().forEach(function (brandHandle) {
        if (brands[brandHandle]) return;
        var label = catalogBrandLabels[brandHandle];
        if (q !== normalizeText(label) && q !== normalizeText(brandHandle)) return;
        results.push({
          kind: 'brand',
          score: BRAND_EXACT_SCORE,
          title: label,
          meta: 'Marque · Voir toutes les pièces',
          url: finderFilterUrl({ brand: brandHandle }),
          brand_handle: brandHandle
        });
      });

      var scopedModel = bestModel && bestModel.score >= 105 ? bestModel.handle : '';
      // Cadre modele : la requete entiere (« joint ») est cherchee dans CE modele, comme si
      // elle le citait ; une requete qui designe elle-meme un modele garde ce modele.
      var parCadre = false;
      if (!scopedModel && applique && applique.model) {
        scopedModel = applique.model;
        parCadre = true;
        bestModel = { score: 125, handle: scopedModel, identity: modelIdentity(scopedModel, models[scopedModel], brands), model: models[scopedModel] };
      }
      if (applique && !parCadre && !cadreMarque && !cadreFamille) applique = null;
      if (scopedModel) {
        var model = bestModel.model || {};
        var candidates = Array.isArray(model.candidate_parts) ? model.candidate_parts : [];
        var schemaSeen = {};
        candidates.forEach(function (candidate) {
          var schemaHandle = String(candidate.schema_handle || '');
          if (!schemaHandle || schemaSeen[schemaHandle]) return;
          schemaSeen[schemaHandle] = true;
          results.push({
            kind: 'schema',
            score: 92 - Math.min(Number(candidate.schema_number) || 0, 20) / 100,
            title: 'Schéma ' + (candidate.schema_number || ''),
            meta: bestModel.identity.label + ' · Vue éclatée',
            url: withPreview('/collections/' + schemaHandle),
            model_handle: scopedModel,
            model_label: bestModel.identity.label,
            brand_handle: bestModel.identity.brand_handle
          });
        });
      }

      var reste = parCadre ? q : (scopedModel && mention && mention.handle === scopedModel ? mention.reste : '');
      var cible = reste || q;
      var textes = textesDesPieces(parts);
      var tokens = q.split(' ').filter(Boolean);
      // Mots du vocabulaire qui valident chaque jeton : calcule UNE fois par
      // requete sur le vocabulaire distinct, puis simple appartenance par piece.
      var motsValides = tokens.map(function (token) { return motsQuiValident(token, textes.vocabulaire); });
      // 01/11 (audit 30 profils) : un mot absent de tout le catalogue (« diametre », « granules »
      // quand aucune piece ne les porte) ne vide plus la recherche : il est ignore, les autres
      // mots decident. Un nombre doit etre un mot entier (« 80 » ne trouve plus « 280 »).
      var vocabulaireTexte = ' ' + textes.vocabulaire.join(' ') + ' ';
      var utiles = tokens.map(function (token, i) {
        var needle = normalizeText(token);
        if (!needle) return false;
        if (/^[0-9]+$/.test(needle)) return vocabulaireTexte.indexOf(' ' + needle + ' ') !== -1;
        return motsValides[i].size > 0 || vocabulaireTexte.indexOf(needle) !== -1;
      });
      if (!utiles.some(Boolean)) utiles = tokens.map(function () { return true; });
      var famillesNommees = famillesCitees(tokens, data.familles);
      var reservePieces = [];
      parts.forEach(function (part, index) {
        if (scopedModel && part.model_handle !== scopedModel) return;
        if (cadreMarque && part.brand_handle !== cadreMarque) return;
        if (cadreFamille && part.famille !== cadreFamille) return;
        // Texte normalise UNE fois par chargement (P0 Finder 2026-10-13 :
        // 313 ms par frappe en le recalculant pour 6 001 pieces).
        var texte = textes[index];
        var strong = texte.strong;
        var context = texte.context;
        var manquants = 0;
        var matches = scopedModel || tokens.every(function (token, i) {
          var needle = normalizeText(token);
          if (!utiles[i]) return true;
          var trouve;
          if (/^[0-9]+$/.test(needle)) trouve = texte.mots.indexOf(needle) !== -1;
          else if (!needle || texte.full.indexOf(needle) !== -1) trouve = true;
          else { var valides = motsValides[i]; trouve = valides.size > 0 && texte.mots.some(function (mot) { return valides.has(mot); }); }
          if (!trouve) manquants += 1;
          return manquants <= 1;
        }) && (scopedModel || manquants === 0 || tokens.length >= 3);
        if (!matches) return;
        // 01/11 (audit 30 profils) : une piece a qui il manque UN mot d'une requete de 3 mots
        // ou plus est gardee en reserve (classee apres), servie seulement si aucune n'a tout.
        var reserve = !scopedModel && manquants === 1;

        var score = scopedModel ? 86 : 45;
        if (strong === cible) score += 80;
        else if (strong.indexOf(cible) !== -1) score += 45;
        if (texte.category === cible) score += 55;
        if (context.indexOf(cible) !== -1) score += 25;
        if (reste && reste.split(' ').every(function (mot) { return fuzzyTokenMatch(mot, strong); })) score += 30;
        // 01/11 : la famille nommee en tete de requete passe devant (« joint de porte » :
        // les joints, puis ce qui parle de porte), les autres familles nommees un peu.
        var rangFamille = part.famille ? famillesNommees.indexOf(part.famille) : -1;
        if (rangFamille === 0) score += 40;
        else if (rangFamille > 0) score += 10;
        if (['joint','vitre','deflecteur','grille','poignee','cendrier'].indexOf(texte.category) !== -1) score += 3;

        (reserve ? reservePieces : results).push(Object.assign({}, part, {
          kind: 'part',
          score: score - (reserve ? 40 : 0) - index / 100000,
          meta: 'Pièce' + (part.model_label ? ' · ' + part.model_label : ''),
          url: part.url || finderUrl(part.reference_oem)
        }));
      });
      if (!results.some(function (r) { return r.kind === 'part'; })) results = results.concat(reservePieces);
      // 04/11 (P1 13, critique Impeccable) : la famille nommee EN TETE de requete (« joints liva 6 », « joint de
      // porte ») filtre strictement les pieces : plus de vitres, conduits ou pellets parmi les joints ; les autres mots
      // affinent. Seulement si cette famille a des pieces dans le resultat (jamais un resultat vide par exces de rigueur).
      var familleTete = familleNommeeEnTete(tokens, data.familles);
      if (familleTete && results.some(function (r) { return r.kind === 'part' && r.famille === familleTete; })) {
        results = results.filter(function (r) { return r.kind !== 'part' || r.famille === familleTete; });
      }

      var seen = {};
      var ranked = results
        .sort(function (a, b) { return b.score - a.score || String(a.title).localeCompare(String(b.title), 'fr'); })
        .filter(function (item) {
          var id = item.kind + ':' + (item.url || item.title);
          if (seen[id]) return false;
          seen[id] = true;
          return true;
        });

      if (scopedModel) {
        var modelResults = ranked.filter(function (item) { return item.kind === 'model'; }).slice(0, 1);
        var partBudget = Math.max(1, max - 3);
        var partResults = ranked.filter(function (item) { return item.kind === 'part'; }).slice(0, partBudget);
        var schemaResults = ranked.filter(function (item) { return item.kind === 'schema'; }).slice(0, 2);
        return avecCadre(modelResults.concat(partResults, schemaResults).slice(0, max), applique);
      }

      return avecCadre(ranked.slice(0, max), applique);
    });
  }

  function finderUrl(query) {
    var url = new URL('/pages/trouver-ma-piece', global.location.origin);
    url.searchParams.set('view', 'trouver-piece-v3');
    url.searchParams.set('mode', 'piece');
    url.searchParams.set('q', String(query || '').trim());
    var preview = currentPreviewThemeId();
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.pathname + url.search;
  }

  function finderFilterUrl(filters) {
    var url = new URL('/pages/trouver-ma-piece', global.location.origin);
    url.searchParams.set('view', 'trouver-piece-v3');
    Object.keys(filters || {}).forEach(function (key) {
      if (filters[key]) url.searchParams.set(key, String(filters[key]));
    });
    var preview = currentPreviewThemeId();
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.pathname + url.search;
  }

  function buildSuggestion(part) {
    var a = document.createElement('a');
    a.className = 'k-oem-predictive';
    a.href = part.url || finderUrl(part.reference_oem);

    var ref = document.createElement('strong');
    ref.textContent = part.reference_oem ? 'Réf. ' + part.reference_oem : 'Pièce';
    a.appendChild(ref);

    var title = document.createElement('span');
    title.textContent = part.title;
    a.appendChild(title);

    if (part.model_label) {
      var model = document.createElement('small');
      model.textContent = part.model_label;
      a.appendChild(model);
    }
    return a;
  }

  /* 29/10 (Florian) : « liva6 » ne donnait rien, « liva 6 » oui ; idem « slx20 », « bay5 »...
     Un mot colle qui est EXACTEMENT le nom compact d'un modele publie est remplace par le
     nom ecrit du modele, avant tout autre traitement. Lu dans les donnees du Finder, aucune
     liste ici ; un mot qui n'est le nom d'aucun modele (une reference) reste intact. */
  function degluerModeles(query, models, brands) {
    var mots = normalizeText(query).split(' ').filter(Boolean);
    if (!mots.length) return String(query || '');
    var compacts = null;
    var change = false;
    var sortie = mots.map(function (mot) {
      if (!/[a-z]/.test(mot) || !/[0-9]/.test(mot)) return mot;
      if (!compacts) {
        compacts = {};
        Object.keys(models).forEach(function (h) {
          var id = modelIdentity(h, models[h], brands);
          if (id.compact && id.short.indexOf(' ') !== -1 && !compacts[id.compact]) compacts[id.compact] = id.short;
        });
      }
      if (compacts[mot]) { change = true; return compacts[mot]; }
      return mot;
    });
    return change ? sortie.join(' ') : String(query || '');
  }

  function predictiveLookup(query, limit, cadre) {
    return loadFinderData().then(function (data) {
      var models = data && data.models && typeof data.models === 'object' ? data.models : {};
      return predictiveLookupDegluee(degluerModeles(query, models, brandLabelMap(data)), limit, cadre);
    });
  }

  function predictiveLookupDegluee(q, limit, cadre) {
    var dimension = parseDimensionQuery(q);
    if (dimension) {
      return lookupDimensions(dimension, limit).then(function (matches) {
        // 01/11 (audit 30 profils) : « coude 45 diametre 80 granules » se lisait comme une
        // mesure et ne trouvait rien ; sans piece a cette mesure, la recherche par mots prend le relais.
        if (matches.length) return { kind: 'dimension', matches: matches };
        return lookupText(q, limit, cadre).then(function (texte) { return { kind: 'text', matches: texte }; });
      });
    }
    if (looksTechnical(q)) {
      // « 9000 », « Bay 5 », « SLX 20 » ressemblent a une reference mais sont
      // des modeles : aucun nom de modele n'est aussi une reference du
      // catalogue (verifie par tests/js/k-search-quality.test.js).
      return loadFinderData().then(function (data) {
        var models = data && data.models && typeof data.models === 'object' ? data.models : {};
        var cite = modeleCite(normalizeText(q), models, brandLabelMap(data));
        if (cite && !cite.reste) {
          return lookupText(q, limit, cadre).then(function (matches) { return { kind: 'text', matches: matches }; });
        }
        return lookupOemSmart(q, limit).then(function (matches) {
          if (matches.length) return { kind: 'oem', matches: matches };
          // 04/11 : « LOTUS-12818 » (marque collee a la reference) n'est la reference d'aucune piece ;
          // la recherche par mots la trouve (« lotus 12818 »), comme pour une mesure sans piece.
          return lookupText(q, limit, cadre).then(function (texte) { return { kind: 'text', matches: texte }; });
        });
      });
    }
    // Reference courte exacte (« 167 ») : trop courte pour paraitre technique,
    // mais si le catalogue la porte telle quelle, c'est elle qu'on cherche.
    if (/[0-9]/.test(q) && normalizeOem(q).length >= 3) {
      return lookupExactOem(q).then(function (exactes) {
        if (exactes.length) return { kind: 'oem', matches: exactes.slice(0, Math.max(1, Number(limit) || 8)) };
        return lookupText(q, limit, cadre).then(function (matches) { return { kind: 'text', matches: matches }; });
      });
    }
    return lookupText(q, limit, cadre).then(function (matches) {
      return { kind: 'text', matches: matches };
    });
  }

  /* Point d'entree UNIQUE de tous les champs de recherche (k-live-search.js). `cadre`
     (facultatif) = contexte de la page ou « Mon appareil », voir cadreApplicable. Une
     requete avec un chiffre (reference, nom de modele) cherche toujours partout. */
  function lookupLive(query, limit, cadre) {
    var q = String(query || '').trim();
    var max = Math.max(1, Number(limit) || 10);
    if (q.length < 3) return Promise.resolve([]);
    var cadreRetenu = cadre && !/[0-9]/.test(q) ? cadre : null;
    return predictiveLookup(q, max, cadreRetenu).then(function (payload) {
      var matches = payload && Array.isArray(payload.matches) ? payload.matches : [];
      return avecCadre(matches, matches.cadre || null);
    });
  }

  function enhancePredictiveInput(input) {
    if (!(input instanceof HTMLInputElement) || input.name !== 'q') return;
    var form = input.form;
    if (!form) return;

    var action;
    try { action = new URL(form.action || '/search', global.location.origin); }
    catch (_e) { return; }
    if (action.pathname !== '/search') return;

    var oldTimer = inputTimers.get(input);
    if (oldTimer) global.clearTimeout(oldTimer);

    var q = input.value.trim();
    var host = form.closest('predictive-search');
    var results = host ? host.querySelector('[data-predictive-search]') : null;
    if (!results) return;

    results.querySelectorAll('[data-k-oem-predictive]').forEach(function (node) { node.remove(); });
    if (!looksTechnical(q) && !looksDimension(q)) return;

    inputTimers.set(input, global.setTimeout(function () {
      predictiveLookup(q, 5).then(function (payload) {
        if (!payload || input.value.trim() !== q) return;
        results.querySelectorAll('[data-k-oem-predictive]').forEach(function (node) { node.remove(); });

        var box = document.createElement('div');
        box.setAttribute('data-k-oem-predictive', '');
        box.className = 'k-oem-predictive-box';

        if (payload.matches.length) {
          var heading = document.createElement('div');
          heading.className = 'k-oem-predictive-heading';
          heading.textContent = payload.kind === 'dimension' ? 'Pièces correspondant à cette taille' : 'Références du fabricant';
          box.appendChild(heading);
          payload.matches.forEach(function (part) { box.appendChild(buildSuggestion(part)); });
        } else {
          var finder = document.createElement('a');
          finder.className = 'k-oem-predictive k-oem-predictive--fallback';
          finder.href = finderUrl(q);
          finder.textContent = payload.kind === 'dimension'
            ? 'Rechercher cette taille dans le Finder'
            : 'Rechercher cette référence dans le Finder';
          box.appendChild(finder);
        }

        results.prepend(box);
        results.removeAttribute('hidden');
      });
    }, 260));
  }

  document.addEventListener('input', function (event) {
    var target = event.target;
    if (target instanceof HTMLInputElement && target.type === 'search') enhancePredictiveInput(target);
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('.k-fum-home__search')) return;

    var action;
    try { action = new URL(form.action || '', global.location.origin); }
    catch (_e) { return; }
    if (action.pathname !== '/search') return;

    var input = form.querySelector('input[name="q"]');
    var q = input ? input.value.trim() : '';
    var dimension = parseDimensionQuery(q);
    if (!dimension && !looksTechnical(q)) return;

    event.preventDefault();
    if (dimension) {
      global.location.assign(finderUrl(q));
      return;
    }

    lookupExactOem(q).then(function (matches) {
      var urls = Array.from(new Set(matches.map(function (part) { return part.url; }).filter(Boolean)));
      if (urls.length === 1) global.location.assign(urls[0]);
      else global.location.assign(finderUrl(q));
    }).catch(function () {
      global.location.assign(finderUrl(q));
    });
  }, true);

  global.KrepiteSearch = {
    version: '2026-10-14-v5',
    normalizeText: normalizeText,
    normalizeLoose: normalizeLoose,
    normalizeOem: normalizeOem,
    normalizeOemQuery: normalizeOemQuery,
    fuzzyTokenMatch: fuzzyTokenMatch,
    parseDimensionQuery: parseDimensionQuery,
    looksDimension: looksDimension,
    looksTechnical: looksTechnical,
    extractDimensionsFromText: extractDimensionsFromText,
    partMatchesDimension: partMatchesDimension,
    matchesDimensionText: matchesDimensionText,
    lookupDimensions: lookupDimensions,
    lookupExactOem: lookupExactOem,
    lookupOemPrefix: lookupOemPrefix,
    lookupOemSmart: lookupOemSmart,
    lookupText: lookupText,
    lookupLive: lookupLive,
    loadFinderData: loadFinderData,
    loadCatalogIndex: loadCatalogIndex,
    loadIndex: loadIndex,
    finderFilterUrl: finderFilterUrl,
    rehydrateFinderData: rehydrateFinderData,
    flattenCatalogIndex: flattenCatalogIndex,
    resolveCatalogContext: resolveCatalogContext,
    predictiveLookup: predictiveLookup,
    finderUrl: finderUrl,
    withPreview: withPreview,
    commerceDisplay: commerceDisplay
  };
})(window);
